import { useState } from 'react'
import { Button, Modal, ModalContent, useDisclosure } from '@heroui/react'
import {
  ReturnsCalculatedSummaryReturnedItem,
  ReturnsTrackingInfoResponseUploadDocumentsInfo
} from '@itsrever/rever-api'
import { useTranslation } from 'react-i18next'
import { usePostHog } from '@posthog/react'
import { CheckIcon } from '@/ui/icons/check'
import { UploadPhotosDialog } from './upload-photos-dialog'
import { itemsRequiringPhotos } from './items-requiring-photos'
import { ItemHeader } from './item-header'

interface UploadPhotosProps {
  uploadDocumentsInfo: ReturnsTrackingInfoResponseUploadDocumentsInfo
  returnedItems?: ReturnsCalculatedSummaryReturnedItem[]
}

export const UploadPhotos = ({
  uploadDocumentsInfo,
  returnedItems
}: UploadPhotosProps) => {
  const { t } = useTranslation()
  const posthog = usePostHog()
  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const [selectedLineItemId, setSelectedLineItemId] = useState<string>()

  const uploadedCount = uploadDocumentsInfo.uploaded_documents?.length ?? 0
  const items = itemsRequiringPhotos(
    returnedItems,
    uploadDocumentsInfo.uploaded_documents
  )
  const selectedItem = items.find(
    (item) => item.lineItemId === selectedLineItemId
  )

  const handleOpen = (lineItemId?: string) => {
    posthog?.capture('Upload Photos Started')
    setSelectedLineItemId(lineItemId)
    onOpen()
  }

  const allSent =
    items.length > 0 && items.every((item) => item.uploadedCount > 0)

  const uploadButton = (lineItemId?: string, sent = false) => (
    <Button
      variant="bordered"
      radius="sm"
      size="sm"
      className="shrink-0 bg-white font-medium"
      onPress={() => handleOpen(lineItemId)}
    >
      {sent ? t('tracking_new.upload_more_cta') : t('tracking_new.upload_cta')}
    </Button>
  )

  const summaryHint = () => {
    if (allSent) return t('tracking_new.upload_done_hint')
    if (items.length === 0 && uploadedCount > 0)
      return t('tracking_new.upload_already', { count: uploadedCount })
    return t('tracking_new.upload_hint')
  }

  const summary = (
    <div className="space-y-1">
      <p className="text-sm font-medium">
        {allSent
          ? t('tracking_new.upload_done_title')
          : t('tracking_new.upload_required_title')}
      </p>
      <p className="mt-0.5 text-xs font-normal">{summaryHint()}</p>
    </div>
  )

  const dialog = (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      scrollBehavior="inside"
      backdrop="blur"
      size="lg"
      className="rounded-md border border-solid border-neutral-200"
      classNames={{ backdrop: 'backdrop-blur-sm' }}
    >
      <ModalContent>
        {(onClose) => (
          <UploadPhotosDialog onClose={onClose} item={selectedItem} />
        )}
      </ModalContent>
    </Modal>
  )

  if (items.length === 0) {
    return (
      <section className="flex w-full items-center justify-between gap-8 border-l-2 border-blue-400 bg-blue-50 p-5 shadow-sm">
        {summary}
        {uploadButton()}
        {dialog}
      </section>
    )
  }

  return (
    <section className="flex w-full flex-col gap-4 border-l-2 border-blue-400 bg-blue-50 p-5 shadow-sm">
      {summary}
      <ul className="flex flex-col gap-3">
        {items.map((item) => (
          <li
            key={item.lineItemId}
            aria-label={item.name}
            className="flex flex-wrap items-center justify-between gap-3"
          >
            <ItemHeader item={item} />
            <div className="ml-auto flex shrink-0 flex-col items-end gap-1.5">
              {item.uploadedCount > 0 ? (
                <p className="flex items-center gap-1 text-xs font-medium text-success-dark">
                  <CheckIcon className="size-4 shrink-0" />
                  {t('tracking_new.upload_already', {
                    count: item.uploadedCount
                  })}
                </p>
              ) : null}
              {uploadButton(item.lineItemId, item.uploadedCount > 0)}
            </div>
          </li>
        ))}
      </ul>
      {dialog}
    </section>
  )
}
